import { zodResolver } from "@hookform/resolvers/zod";
import { useSession } from "next-auth/react";
import { useEffect, useId, useRef } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { constants } from "~/common/constants";
import { useAnalytics } from "~/components/Analytics";
import { ConditionalError } from "~/components/ConditionalError";
import { NewsletterPrivacyPolicy } from "~/components/NewsletterPrivacyPolicy";
import { useToastManager } from "~/components/ToastManager";
import { TurnstileWidget } from "~/components/TurnstileWidget";
import { primaryButtonStyle } from "~/landing-pages/components/CTA/cta-shared-styles";
import {
  eyebrowColorStyle,
  inputStyle,
  secondaryTextColorStyle,
} from "~/landing-pages/shared-landing-page-styles";
import { upsertEmailLists } from "~/lib/client/endilApiCalls";
import { useTurnstileHelper } from "~/lib/client/useTurnstileHelper";
import { getValueOrDefault } from "~/lib/shared/getValueOrDefault";
import { EMAIL_FIELD_SCHEMA } from "~/lib/shared/schemas/fieldSchemas";
import { Body } from "~/ui-library";

const createSchema = (enableTurnstile: boolean) =>
  z.object({
    email: EMAIL_FIELD_SCHEMA,
    [constants.TURNSTILE_RESULT_PROPERTY]: enableTurnstile
      ? z.string()
      : z.string().nullable().optional(),
  });

type NewsletterFormData = z.infer<ReturnType<typeof createSchema>>;

export type NewsletterSignupFormProps = {
  appearance: "brand" | "dark" | "light";
  blockIndex: number;
  className?: string;
  /** iterable id used to update the user's newsletter preferences */
  iterableListId: number;
  /** slug used for event tracking */
  newsletterSlug: string;
};
export const NewsletterSignupForm: React.FC<NewsletterSignupFormProps> = ({
  appearance,
  blockIndex,
  className,
  iterableListId,
  newsletterSlug,
}) => {
  const init = useRef(false);

  const { data: session, status } = useSession();

  const {
    enableTurnstile,
    resetTurnstile,
    setTurnstileResult,
    trackTurnstileError,
  } = useTurnstileHelper();

  const {
    register,
    handleSubmit,
    getValues,
    formState: { isValid, isSubmitting, errors },
    reset,
    setValue,
  } = useForm<NewsletterFormData>({
    defaultValues: {
      email: "",
    },
    mode: "onBlur",
    resolver: zodResolver(createSchema(enableTurnstile)),
  });

  const { analyticsEnhancedTrack } = useAnalytics();
  const { addToast } = useToastManager();

  const id = useId();

  useEffect(() => {
    if (
      !init.current &&
      status !== "loading" &&
      session &&
      !getValues().email
    ) {
      init.current = true;
      setValue("email", getValueOrDefault(session.user.email, ""), {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [getValues, init, session, setValue, status]);

  const onSubmit: SubmitHandler<NewsletterFormData> = async ({
    email,
    turnstileResult,
  }) => {
    try {
      analyticsEnhancedTrack("Landing Page Newsletter Sign-Up", {
        blockIndex,
        newsletter: newsletterSlug,
      });

      await upsertEmailLists({
        email,
        listIds: [iterableListId],
        turnstileResult,
      });
      reset({
        email: "",
      });
      setValue("email", email, { shouldDirty: true });

      resetTurnstile();
      setValue("turnstileResult", undefined);
      addToast("success", "You are now subscribed to the newsletter!");
    } catch (err) {
      resetTurnstile();
      setValue("turnstileResult", undefined);
      addToast("danger", "Something went wrong, please try again!");
    }
  };

  return (
    <form className={className} onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-2 gap-4">
        <label htmlFor={id} className="sr-only">
          Email address
        </label>
        <div className="col-span-2">
          <input
            disabled={status === "loading"}
            placeholder="Enter your email"
            id={id}
            {...register("email", { required: true })}
            className={inputStyle({ appearance, className: "w-full" })}
          />
          <ConditionalError error={errors.email?.message} />
        </div>
        <div className="col-span-2 flex gap-4">
          {enableTurnstile ? (
            <TurnstileWidget
              onVerify={(token) => {
                setValue("turnstileResult", token, { shouldValidate: true });
                setTurnstileResult(token);
              }}
              onError={trackTurnstileError}
            />
          ) : null}
          <button
            type="submit"
            className={primaryButtonStyle({
              appearance,
              className: "h-fit w-full self-center xs:py-3",
              disabled: !isValid || isSubmitting,
            })}
            disabled={!isValid || isSubmitting}
          >
            Sign Up
          </button>
        </div>
      </div>

      <Body
        size="small"
        className={secondaryTextColorStyle({
          appearance,
          className: "mt-m max-w-[560px]",
        })}
      >
        <NewsletterPrivacyPolicy
          linkClassName={eyebrowColorStyle({
            appearance,
            className: "font-semibold",
          })}
          preamble="By clicking “Sign Up”,"
        />
      </Body>
    </form>
  );
};
